import cx from 'clsx';
import style from './styles.module.scss';

import React, { memo, useEffect, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useNavigate } from '@reach/router';
import { parse, stringify } from 'query-string';

import { Button, ButtonVariant } from '@clover/web-lib-consumer-ui';
import { Icon } from 'components/Icon';
import { LineItems } from '../../components/LineItems/LineItems';
import { formatAddress, formatPhoneNumber } from 'util/Formatting';
import { useMerchant } from 'hooks/context/merchant';
import { useServiceType } from 'hooks/context/serviceType';
import { PageLayout } from 'components/PageLayout';
import { RouteFC } from 'util/routeTypes';
import { CONFIRMATION_TEST_ID } from './const';
import { ScrollToTop } from 'components/ScrollToTop';
import { Map } from 'components/Map';
import { MerchantDetails } from 'olo-service';
import { OLO_IPG_TOKEN } from 'util/IpgPay';
import { useCartDispatch } from 'hooks/context/cart';
import { useGiftCardCartDispatch } from 'hooks/context/giftCardCart';
import { Banner, BannerVariant } from 'components/Banner';
import { DetailedCartItem } from 'hooks/context/cart/models';
import { NOTE_TEST_ID } from 'components/ContactInfoForm.const';
import { MerchantTitle } from 'components/MerchantTitle';
import { useMerchantState } from 'hooks/context/MerchantState';
import { GetTheAppLink } from 'components/GetTheAppLink';
import { useOptimizelyTrack } from '@clover-util/optimizely';
import { SessionStorage } from 'util/Storage';
import { COLO_CONTACT_INFO, COLO_CONTACT_NOTE } from 'routes/Checkout/context/OrderDetails';
import { BasicHeader } from 'components/BasicHeader';
import { DeliveryMapBanner } from 'components/DeliveryMapBanner/DeliveryMapBanner';
import { useDelivery } from 'hooks/context/Delivery';
import { useScheduledOrderDisplay } from 'hooks/context/ScheduledOrderDisplay';
import { heapTrack } from 'util/Analytics';
import { useCustomer } from 'hooks/context/customer/customer';

const ThanksForShopping: React.FC<{restaurant: string}> = ({ restaurant, children }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <>
      <p>
        <Trans i18nKey="success.shopping">
          Thanks for shopping at <strong data-testid={CONFIRMATION_TEST_ID.merchantName}>{{ restaurant }}</strong>.
        </Trans>
      </p>
      <Button data-testid={CONFIRMATION_TEST_ID.redirectToGiftCardsBtn} className={style.redirectButton} variant={ButtonVariant.SECONDARY} onClick={() => navigate('./', { replace: true })}>
        <span>{t('giftCards.getAnotherGiftCard')}</span>
      </Button>
      {children}
    </>
  );
};

const PickupInfo: React.FC<{merchant: MerchantDetails}> = ({ merchant, children }) => {
  const { t } = useTranslation();
  const { name: merchantName, address, phone } = merchant;

  return (
    <div>
      <p>
        <Trans i18nKey="success.pickUp">
          Pick up at <strong data-testid={CONFIRMATION_TEST_ID.merchantName}>{{ restaurant: merchantName }}</strong>
        </Trans>
      </p>
      <p data-testid={CONFIRMATION_TEST_ID.merchantAddress}>
        {t('address.oneLine', address)}
      </p>
      {phone && <a className="phoneNumber" data-testid={CONFIRMATION_TEST_ID.merchantPhone} href={`tel:${phone}`}>{formatPhoneNumber(phone)}</a>}
      {children}
    </div>
  );
};

const DineInPlaceOrder: React.FC<{ items?: DetailedCartItem[]; }> = memo(({ items }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className={style.dineInPlaceOrderWrapper}>
      {(items?.length ?? 0) > 0 && (
        <div className={style.lineItemsSection}>
          <LineItems readOnly items={items} />
        </div>
      )}
      <div data-testid={CONFIRMATION_TEST_ID.wantSomethingElseText} className={style.textSection}>
        <span>{t('dineIn.wantSomethingElse')}</span>
      </div>
      <Button data-testid={CONFIRMATION_TEST_ID.placeAnotherOrderBtn} className={style.placeAnotherOrderButton} onClick={() => navigate('./', { replace: true })}>
        <h3>{t('dineIn.placeAnotherOrder')}</h3>
      </Button>
    </div>
  );
});

type ReceiptLocationState = {
  receiptId?: string;
  hasGiftCardsOnly?: boolean;
  giftCardRecipientEmail?: string;
  orderItems?: DetailedCartItem[];
  estimatedTime: string;
  merchantNote?: string;
  deliveryTrackingUrl?: string;
}

export const isReceiptLocationState = (state?: any): state is ReceiptLocationState => {
  return !!(state?.receiptId);
};

const GA_PLAY_STORE_REFERRER = {
  referrer: stringify({
    utm_source: 'CWO',
    utm_medium: 'confirmation_page',
  }),
};

export const Confirmation: RouteFC = ({ location }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { merchant } = useMerchant();
  const { resetServiceTypeData, tableDetails } = useServiceType();
  const { showDineInState, showRunningOrderState } = useMerchantState();
  const state = location?.state;
  const { receiptId: receiptIdFromPath, estTime: estimatedTimeFromPath, processor, error } = parse(location?.search ?? '');
  const { giftCardRecipientEmail, hasGiftCardsOnly, receiptId = receiptIdFromPath, orderItems, estimatedTime : estimatedTimeFromState, merchantNote } = state as ReceiptLocationState || {};
  const redirectFromIpg = processor === OLO_IPG_TOKEN.toLowerCase();
  const { clear: clearCartItems } = useCartDispatch();
  const { clear: clearGiftCards } = useGiftCardCartDispatch();
  const { resetScheduledOrderState } = useScheduledOrderDisplay();
  const track = useOptimizelyTrack();
  const deliveryStatusLink = isReceiptLocationState(state) ? state.deliveryTrackingUrl : undefined;

  const showDelivery = deliveryStatusLink;
  const { deliveryAddress } = useDelivery();
  const { stayLoggedIn, setChosenStayLoggedIn } = useCustomer();
  const estimatedTime = estimatedTimeFromState ? estimatedTimeFromState : estimatedTimeFromPath;

  useEffect(() => {
    resetScheduledOrderState();
    if (!isReceiptLocationState(state) && !(redirectFromIpg && receiptIdFromPath)) {
      navigate('./', { replace: true });

      return;
    }

    if (redirectFromIpg && !error) {
      clearCartItems();
      clearGiftCards();
      resetServiceTypeData();
      // Clear Contact Info
      SessionStorage.tryRemoveItem(COLO_CONTACT_INFO);
      SessionStorage.tryRemoveItem(COLO_CONTACT_NOTE);
    }
  }, [clearCartItems, resetServiceTypeData, clearGiftCards, error, navigate, receiptIdFromPath, redirectFromIpg, state, resetScheduledOrderState, setChosenStayLoggedIn, stayLoggedIn]);

  useEffect(() => {
    if (!merchant) return;

    track('conversion');
  }, [track, merchant]);

  useEffect(() => {
    if (showRunningOrderState) {
      heapTrack('S2O-order-complete-page-visit');
    }
  }, [showRunningOrderState]);

  useEffect(() => {
    setChosenStayLoggedIn(!!stayLoggedIn);
  }, [stayLoggedIn, setChosenStayLoggedIn]);

  const appLinkQuery = useMemo(() => ({
    ...GA_PLAY_STORE_REFERRER,
    merchantId: merchant?.merchantUuid,
    receiptId,
  }), [merchant?.merchantUuid, receiptId]);

  if (!merchant) return null;

  if (showRunningOrderState){
  return (
    <ScrollToTop>
      <PageLayout
        header={<BasicHeader to="./" />}
        forceStandardClasses
        customMainClass={cx(style.wrapper, style.spaceIncrease)}
        fullWidth>
        <section className={cx(style.section)}>
          <summary>
            <Icon aria-hidden={true} name={'circleCheck'} size={64} />
            <h2 className={style.title}>
            <Trans i18nKey="dine_in_success.dine_in_thank_you">
              Thank You! <br /> Come again
            </Trans>
            </h2>
            <p className={style.message}>
            {t('dine_in_success.dine_in_bill_pay')}
            </p>
            <p>
              <Trans i18nKey="success.viewReceipt">
                You can view your receipt
                <a
                  data-testid={CONFIRMATION_TEST_ID.receiptDineInLink}
                  href={(isReceiptLocationState(state) || receiptIdFromPath) ? `/r/${receiptId}` : '#'}
                >
                  here
                </a>.
              </Trans>
            </p>
            <p>
            <Trans i18nKey="dine_in_success.dine_in_appreciate">
              We appreciate your patronage and hope <br /> you have a wonderful day!
            </Trans>
            </p>
          </summary>
        </section>
        </PageLayout>
    </ScrollToTop>
  );
  }

  return (
    <ScrollToTop>
      <PageLayout
        header={<BasicHeader to="./" />}
        forceStandardClasses
        customMainClass={cx(style.wrapper, {
          [style.fullHeight]: showDineInState
        })}
        fullWidth>
        <MerchantTitle i18nKey="helmet.titleConfirmation" />
         <section className={cx(style.section, { [style.dineInSection]: showDineInState })}>
          <summary>
            <Icon aria-hidden={true} name={'shoppingbag'} size={64} />
            {!showDelivery ? <h2 className={style.title}>{t('hoorah')}</h2> :
             <h2 className={cx(style.title, style.deliveryTitle)}>
                <Trans i18nKey="success.orderPrep">
                  Your order is being <br /> prepared!
                </Trans>
              </h2>}

            {!hasGiftCardsOnly && !showDelivery && <p data-testid={CONFIRMATION_TEST_ID.dineInText} className={style.message}>{
              t(`success.${showDineInState ? `dine_in_${tableDetails?.name ? 'table_' : ''}` : ''}message`, { estimatedTime, tableNumber: tableDetails?.name })}
            </p>}

            {showDelivery && <p data-testid={CONFIRMATION_TEST_ID.deliveryText} className={style.message}>{
              t('success.deliveryMessage', { estimatedTime })}
            </p>}

            {merchantNote && !showDelivery && <p className={style.message} data-testid={NOTE_TEST_ID}>{merchantNote}</p>}

            {giftCardRecipientEmail && <p>{t('success.gift_card_sent', { recipientEmail: giftCardRecipientEmail })}</p>}

            <p>
              <Trans i18nKey="success.viewReceipt">
                You can view your receipt
                <a
                  data-testid={CONFIRMATION_TEST_ID.receiptLink}
                  href={(isReceiptLocationState(state) || receiptIdFromPath) ? `/r/${receiptId}` : '#'}
                >
                  here
                </a>.
              </Trans>
            </p>
          </summary>
          {hasGiftCardsOnly && <ThanksForShopping restaurant={merchant.name} />}
          {showDineInState && <DineInPlaceOrder items={orderItems} />}
          {!hasGiftCardsOnly && !showDineInState && !showDelivery && <PickupInfo merchant={merchant} />}
          {showDelivery && <div>
            <p>{t('success.deliveringTo')}</p>
            <p data-testid={CONFIRMATION_TEST_ID.customerAddress}>{formatAddress(deliveryAddress)}</p>
            {deliveryAddress?.address2 && <p>{deliveryAddress?.address2}</p>}
          </div>}
        </section>

        {!showDineInState && !showDelivery && !showRunningOrderState && <section className={style.mapSection}>
            {merchant.locale === 'en-US' &&
              <Banner className={style.banner} variant={BannerVariant.INFO}>
                <Icon className={style.icon} name="phone" />
                <p>
                  <Trans i18nKey="success.banner.appPromo">
                    Easy checkout. Easy reordering.<br /> Use the <GetTheAppLink className={style.link} query={appLinkQuery}>Clover app</GetTheAppLink> for Android and iOS.
                  </Trans>
                </p>
              </Banner>}
              {!showDelivery && <Map className={style.mapContainer} address={merchant.address} data-testid={CONFIRMATION_TEST_ID.mapElement} />}
          </section>}
          {deliveryStatusLink && <DeliveryMapBanner className={style.deliveryBanner} href={deliveryStatusLink} />}
      </PageLayout>
    </ScrollToTop>
  );
};
